/* ============================================================================
   Anthropoiid — site-wide autoplay video controller.

   WHY THIS EXISTS
   ---------------
   Decorative tile videos are rendered WITHOUT an `autoplay` attribute (see
   snippets/anthropoiid-autoplay-video.liquid) and are started from here
   instead. That is not a stylistic choice — it is the fix for the play-button
   bug:

     WebKit's HTMLMediaElement::shouldForceControlsDisplay() returns true for
     `isVideo() && autoplay() && RequireUserGestureForVideoDueToLowPowerMode`
     (or the equivalent thermal restriction). On an iPhone/iPad in Low Power
     Mode — or merely a hot one — that makes Safari inject its own media
     controls over the video even though `controls` was never set, which is the
     big circular play button. Nothing in author CSS can hide it: the old
     ::-webkit-media-controls-start-playback-button pseudo-element was removed
     from WebKit around Safari 604 / iOS 11, and the modern controls live in a
     UA shadow root. Removing the attribute is the only thing that works.

   On top of that, every engine layers its own policy on the `play()` call:

     • WebKit/iOS   — Low Power Mode and Accessibility ▸ Motion ▸ "Auto-Play
                      Video Previews" both block video regardless of `muted`;
                      macOS Safari adds per-site "Never Auto-Play".
     • Chrome/Edge  — muted autoplay is always allowed, but a video paused
                      while off-screen is not guaranteed to resume by itself.
     • Firefox      — allows muted by default; "Block Audio and Video" does not.
     • In-app views — Instagram/Facebook webviews inherit the host app's media
                      policy, which may block everything. (Primary traffic
                      source for this brand, so this case is not hypothetical.)

   So: drive playback ourselves, verify it really happened, retry forever, and
   make refusal look intentional rather than broken.

   DESIGN NOTES
   ------------
   1. VERIFY, DON'T TRUST. A resolved play() promise does not prove pixels are
      moving — some engines resolve then immediately stall. Every start is
      confirmed by watching `currentTime` actually advance.
   2. UNLOCK ON GESTURE, SYNCHRONOUSLY. WebKit clears the restriction
      permanently, per element, but only if play() is called while the user
      gesture is still being processed — so the handler must run play() inline,
      never behind a timeout, promise or rAF. The events that count are
      `click`, `touchend` and `keydown`; `touchstart`/`pointerdown` do not.
   3. GRACEFUL LAST RESORT. If playback is refused for good, the tile settles
      on its poster. On Apple devices we go one better and swap in the same MP4
      as an ANIMATED IMAGE — Safari decodes H.264 through the image pipeline
      (Apple's documented GIF replacement), which never constructs a media
      session and so is not subject to autoplay policy at all. That restores
      real motion even in Low Power Mode. It self-reverts to the poster if the
      browser cannot decode it.
   4. BUDGET DECODERS. Only videos near the viewport play. WebKit lifted its
      hard concurrency limit for silent video in 2016, but memory and decode
      pressure are real, so a generous cap still applies, prioritised by
      distance from the viewport centre.
   5. IDEMPOTENT + DYNAMIC. Safe to run twice; a MutationObserver adopts videos
      injected later (quick view, AJAX cart, Section Rendering API).

   NOT DONE ON PURPOSE: no `video.load()` on a failed play(). WebKit only clears
   restrictions in prepareForLoad() while a gesture is being processed, and a
   promise rejection is already a microtask later — so it cannot help, and it
   throws away buffered data and refetches. It is a pure regression.

   SCOPE: `video[data-ant-av]` plus the PDP gallery via window.AnthropoiidAV.play.
   The theme's own Alpine-driven videos (Theme_Video / HTMLVideo) are untouched.

   DEBUG: window.AnthropoiidAV.report() → per-video state table.
   ========================================================================== */
(function () {
  'use strict';

  var SEL = 'video[data-ant-av]';

  /* --- environment -------------------------------------------------------- */

  var ua = navigator.userAgent || '';
  // iPadOS reports itself as "MacIntel" with touch points, so sniff both.
  var IS_APPLE =
    /iP(hone|od|ad)/.test(ua) ||
    (navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1) ||
    (/Safari/.test(ua) && !/Chrome|Chromium|CriOS|FxiOS|Edg|Android/.test(ua));

  // WebKit removed its "one silent video at a time" restriction in 2016, so
  // this is about memory and decode pressure, not policy. Keep it well clear
  // of any realistic tile count so a visible tile is never starved.
  var MAX_CONCURRENT = IS_APPLE ? 6 : 10;

  // Start work slightly before a tile scrolls in, so it is already running by
  // the time it is actually looked at.
  var NEAR_MARGIN = 400;

  var MAX_BACKOFF_TRIES = 5;

  /* --- helpers ------------------------------------------------------------ */

  function each(list, fn) {
    for (var i = 0; i < list.length; i++) fn(list[i], i);
  }

  function all(root) {
    return (root || document).querySelectorAll(SEL);
  }

  function state(v) {
    if (!v.__antAV) {
      // `budget` marks a pause WE chose (too many videos near the viewport at
      // once) as opposed to one the browser imposed. The two must never be
      // confused: a budget pause is healthy and must not be retried or counted
      // as a refusal.
      v.__antAV = {
        near: false,
        tries: 0,
        timer: 0,
        watchdog: 0,
        dead: false,
        budget: false,
        img: null
      };
    }
    return v.__antAV;
  }

  function rect(v) {
    try {
      return v.getBoundingClientRect();
    } catch (e) {
      return null;
    }
  }

  function viewportH() {
    return window.innerHeight || document.documentElement.clientHeight || 0;
  }

  function isNear(v, pad) {
    var r = rect(v);
    if (!r) return false;
    if (r.width === 0 && r.height === 0) return false; // display:none / detached
    pad = pad == null ? NEAR_MARGIN : pad;
    var h = viewportH();
    var w = window.innerWidth || document.documentElement.clientWidth || 0;
    return r.bottom > -pad && r.top < h + pad && r.right > -pad && r.left < w + pad;
  }

  // How far the element's centre sits from the viewport centre — decides who
  // wins the decoder budget when more tiles are near than we allow.
  function distance(v) {
    var r = rect(v);
    if (!r) return Infinity;
    return Math.abs(r.top + r.height / 2 - viewportH() / 2);
  }

  function flag(v, value) {
    if (value) v.setAttribute('data-ant-av-state', value);
    else v.removeAttribute('data-ant-av-state');
  }

  function clearTimers(s) {
    if (s.timer) {
      clearTimeout(s.timer);
      s.timer = 0;
    }
    if (s.watchdog) {
      clearTimeout(s.watchdog);
      s.watchdog = 0;
    }
  }

  /* --- last-resort motion: the MP4 as an animated image ------------------- */

  // Safari can decode H.264 through the IMAGE pipeline (Apple documents this
  // as the modern replacement for animated GIFs). That path never constructs a
  // media session, so Low Power Mode / "Auto-Play Video Previews" cannot block
  // it. Only attempted once a video is definitively refused, and only for the
  // tile the shopper is actually looking at, so it costs nothing on the happy
  // path. Reverts to the poster if the browser cannot decode it.
  function animatedImageFallback(v) {
    var s = state(v);
    if (s.img || !IS_APPLE || !s.near) return;
    var source = v.querySelector('source');
    var src = source && source.src;
    if (!src) return;

    var img = document.createElement('img');
    // Inherit the tile's layout/object-fit classes, minus our own hook and
    // minus `track-loaded` (the theme keeps img.track-loaded at opacity 0
    // until its own loader marks it complete — which would hide this).
    img.className =
      v.className
        .split(/\s+/)
        .filter(function (c) {
          return c && c !== 'ant-av' && c !== 'track-loaded';
        })
        .join(' ') + ' ant-av-img';
    img.alt = '';
    img.setAttribute('aria-hidden', 'true');
    img.setAttribute('decoding', 'async');

    img.addEventListener('load', function () {
      v.style.display = 'none';
      flag(v, 'animated-image');
    });
    img.addEventListener('error', function () {
      // Not decodable as an image on this browser — the poster stands.
      if (img.parentNode) img.parentNode.removeChild(img);
      s.img = null;
      v.style.removeProperty('display');
      flag(v, 'poster');
    });

    s.img = img;
    img.src = src;
    if (v.parentNode) v.parentNode.insertBefore(img, v);
  }

  /* --- playback ----------------------------------------------------------- */

  // Re-assert the properties WebKit checks on every attempt. Alpine morphs,
  // bfcache restores and section re-renders can all quietly reset them.
  function prime(v) {
    try {
      v.muted = true;
      v.defaultMuted = true;
      v.playsInline = true;
      v.loop = true;
      v.controls = false;
    } catch (e) {}
  }

  // Confirm the video is genuinely advancing, not merely "not paused".
  // This distinction matters: on a slow connection a video reports
  // paused === false with readyState HAVE_CURRENT_DATA and never moves a
  // frame. play() resolved, no error fired, and yet the shopper is looking at
  // a frozen image. Only comparing currentTime across two samples catches it.
  function watch(v) {
    var s = state(v);
    if (s.watchdog) clearTimeout(s.watchdog);
    var before = v.currentTime;
    s.watchdog = setTimeout(function () {
      s.watchdog = 0;
      if (!v.isConnected || !s.near) return;
      if (s.budget) return; // we paused it on purpose; not a refusal
      if (v.paused || v.currentTime === before) {
        // Nothing moved. If the element still believes it is playing, this is
        // a buffering stall, not a policy refusal — a very different problem.
        blocked(v, v.paused ? 'refused' : 'stalled');
      } else {
        s.tries = 0;
        s.dead = false;
        flag(v, 'playing');
      }
    }, 900);
  }

  // reason: 'refused' — the OS/browser declined playback (Low Power Mode,
  //                     "Never Auto-Play", hostile webview). More waiting will
  //                     not help; only a gesture or a different medium will.
  //         'stalled' — playback was permitted but data is not arriving fast
  //                     enough. Waiting IS the answer; keep checking quietly
  //                     and never escalate, because swapping in a heavier
  //                     fallback on a slow link would make things worse.
  function blocked(v, reason) {
    var s = state(v);

    if (reason === 'stalled') {
      flag(v, 'buffering');
      if (s.timer) return;
      s.timer = setTimeout(function () {
        s.timer = 0;
        if (s.near && !document.hidden) play(v);
      }, 2000);
      return;
    }

    if (s.tries >= MAX_BACKOFF_TRIES) {
      // Stop the backoff ladder. A user gesture can still revive it, and on
      // Apple hardware we can still get real motion out of the image pipeline.
      s.dead = true;
      flag(v, 'poster');
      animatedImageFallback(v);
      return;
    }
    flag(v, 'blocked');
    if (s.timer) return;
    var wait = Math.min(300 * Math.pow(2, s.tries), 5000);
    s.timer = setTimeout(function () {
      s.timer = 0;
      if (s.near && !document.hidden) play(v);
    }, wait);
  }

  // MUST stay synchronous from the gesture handler down to v.play(): WebKit
  // only clears the low-power restriction while the gesture is being processed.
  function play(v) {
    if (!v || !v.isConnected) return;
    var s = state(v);
    if (s.img) return; // the animated image has taken over this tile
    s.budget = false;
    s.tries++;
    prime(v);

    var p;
    try {
      p = v.play();
    } catch (e) {
      blocked(v, 'refused');
      return;
    }

    if (p && typeof p.then === 'function') {
      p.then(function () {
        watch(v);
      })['catch'](function () {
        blocked(v, 'refused');
      });
    } else {
      // Pre-promise browsers: the watchdog is the only source of truth.
      watch(v);
    }
  }

  // isBudget: true when this pause is our own decoder-budget decision rather
  // than the tile leaving the viewport.
  function pause(v, isBudget) {
    var s = state(v);
    s.budget = !!isBudget;
    clearTimers(s);
    try {
      if (!v.paused) v.pause();
    } catch (e) {}
    if (!s.img) flag(v, isBudget ? 'queued' : 'paused');
  }

  /* --- decoder budget ----------------------------------------------------- */

  // Play the N nearest in-view videos, pause everything else. Cheap, idempotent.
  var reconcileQueued = false;
  function reconcile() {
    reconcileQueued = false;
    var candidates = [];
    each(all(document), function (v) {
      var s = state(v);
      s.near = isNear(v);
      if (s.img) return; // handed over to the image pipeline
      if (s.near) candidates.push(v);
      else if (!v.paused) pause(v);
    });

    if (candidates.length > MAX_CONCURRENT) {
      candidates.sort(function (a, b) {
        return distance(a) - distance(b);
      });
    }

    each(candidates, function (v, i) {
      if (i < MAX_CONCURRENT) {
        if (v.paused) play(v);
      } else if (!v.paused) {
        pause(v, true); // over budget — deliberate, not a failure
      }
    });
  }

  function queueReconcile() {
    if (reconcileQueued) return;
    reconcileQueued = true;
    if (window.requestAnimationFrame) window.requestAnimationFrame(reconcile);
    else setTimeout(reconcile, 16);
  }

  /* --- observation -------------------------------------------------------- */

  var io =
    'IntersectionObserver' in window
      ? new IntersectionObserver(queueReconcile, {
          rootMargin: NEAR_MARGIN + 'px 0px',
          threshold: [0, 0.01, 0.25]
        })
      : null;

  function themeManaged(v) {
    return !!(
      v.closest &&
      v.closest('.html-video-wrapper, [x-data*="Theme_Video"], [x-data*="HTMLVideo"]')
    );
  }

  function setup(v) {
    if (v.getAttribute('data-ant-av-init') === '1') return;
    if (themeManaged(v)) return;
    v.setAttribute('data-ant-av-init', '1');
    prime(v);
    v.setAttribute('playsinline', '');
    v.setAttribute('webkit-playsinline', '');

    // Media events are the earliest reliable moment to (re)try a blocked video.
    each(['loadeddata', 'canplay', 'canplaythrough'], function (ev) {
      v.addEventListener(
        ev,
        function () {
          var s = state(v);
          if (s.near && v.paused && !s.dead) play(v);
        },
        { passive: true }
      );
    });

    // A dead source must not sit there as a black box — the poster is already
    // the correct first frame, so fall back to it.
    each(['error', 'abort'], function (ev) {
      v.addEventListener(
        ev,
        function () {
          state(v).dead = true;
          flag(v, 'poster');
        },
        { passive: true }
      );
    });

    // Some engines pause a looping video on their own (decode pressure,
    // backgrounded tab). If it is still on screen, start it again.
    v.addEventListener(
      'pause',
      function () {
        var s = state(v);
        if (!s.near || document.hidden || s.dead || s.img || s.budget) return;
        if (v.ended || v.seeking) return;
        setTimeout(function () {
          if (s.near && v.paused && !document.hidden && !s.dead) play(v);
        }, 120);
      },
      { passive: true }
    );

    if (io) io.observe(v);
  }

  function scan(root) {
    each(all(root), setup);
    queueReconcile();
  }

  /* --- revival ------------------------------------------------------------ */

  // Called straight from a user gesture — everything below must stay
  // synchronous so WebKit still counts the gesture when play() is reached.
  function unlock() {
    each(all(document), function (v) {
      var s = state(v);
      s.dead = false;
      s.tries = 0;
      clearTimers(s);
    });
    reconcile();
  }

  function init() {
    scan(document);

    // The events WebKit's autoplay policy recognises as a user gesture.
    // Capture phase so a tile's own link handler cannot swallow them first.
    each(['click', 'touchend', 'keydown'], function (ev) {
      window.addEventListener(ev, unlock, { passive: true, capture: true });
    });

    var scrollTick = 0;
    each(['scroll', 'wheel', 'resize', 'orientationchange'], function (ev) {
      window.addEventListener(
        ev,
        function () {
          if (scrollTick) return;
          scrollTick = setTimeout(function () {
            scrollTick = 0;
            reconcile();
          }, 150);
        },
        { passive: true }
      );
    });

    // Returning to the tab, or restoring from the back/forward cache (very
    // common on mobile: tile → product → back), leaves videos paused.
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) unlock();
    });
    window.addEventListener('pageshow', unlock);
    window.addEventListener('focus', queueReconcile);

    // Theme editor + Section Rendering API.
    each(
      ['shopify:section:load', 'shopify:section:reorder', 'shopify:section:select'],
      function (ev) {
        document.addEventListener(ev, function (e) {
          scan((e && e.target) || document);
        });
      }
    );

    // Quick view, AJAX cart, infinite scroll, Alpine morphs.
    if ('MutationObserver' in window) {
      new MutationObserver(function (muts) {
        var found = false;
        for (var i = 0; i < muts.length; i++) {
          var added = muts[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var n = added[j];
            if (n.nodeType !== 1) continue;
            if (n.matches && n.matches(SEL)) {
              setup(n);
              found = true;
            }
            if (n.querySelectorAll && n.querySelectorAll(SEL).length) {
              scan(n);
              found = true;
            }
          }
        }
        if (found) queueReconcile();
      }).observe(document.documentElement, { childList: true, subtree: true });
    }

    // No IntersectionObserver (very old WebView): poll cheaply instead.
    if (!io) setInterval(reconcile, 800);
  }

  /* --- public API --------------------------------------------------------- */

  window.AnthropoiidAV = {
    // Used by the PDP gallery (sections/main-product.liquid), whose slides are
    // driven by Alpine rather than by visibility — it needs the same hardened
    // start without joining the tile budget.
    play: function (v) {
      if (!v) return;
      var s = state(v);
      s.near = true;
      s.dead = false;
      s.tries = 0;
      clearTimers(s);
      play(v);
    },
    pause: function (v) {
      if (v) pause(v);
    },
    rescan: function (root) {
      scan(root || document);
    },
    unlock: unlock,
    // Diagnostics — used by the automated test harness, handy in the console.
    report: function () {
      var out = [];
      each(all(document), function (v) {
        var r = rect(v) || {};
        var s = state(v);
        out.push({
          src: (v.currentSrc || (v.querySelector('source') || {}).src || '').split('/').pop(),
          state: v.getAttribute('data-ant-av-state'),
          paused: v.paused,
          currentTime: Number(v.currentTime.toFixed(2)),
          readyState: v.readyState,
          networkState: v.networkState,
          muted: v.muted,
          playsInline: v.playsInline,
          hasAutoplayAttr: v.hasAttribute('autoplay'),
          error: v.error ? v.error.code : null,
          near: s.near,
          tries: s.tries,
          budgetPaused: !!s.budget,
          imageFallback: !!s.img,
          w: Math.round(r.width || 0),
          h: Math.round(r.height || 0)
        });
      });
      return out;
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
